import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaGlobe } from "react-icons/fa";
import { useLanguage } from "../../components/LanguageContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Language } from "../../Types/types";
import {
  AppDispatch,
  RootState,
} from "../../components/Manual_List/fetch/store";
import {
  postCategory,
  patchCategory,
  fetchCategoryById,
} from "../../components/Manual_List/fetch/slices";
import { uploadCategoryThumbnail } from "../../components/Manual_List/fetch/service";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import { AddLanguageModal } from "../../components/shared/AddLanguageModal";
import addcategorytranslation from "./TranslationAdd";
import { getBaseUrlWithoutApi } from "../../components/Manual_List/fetch/baseEndpoint";

const AddCategory: React.FC = () => {
  const { language } = useLanguage();
  const t = addcategorytranslation[language as Language];
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const category = useSelector((state: RootState) => state.data.category.data);

  const [names, setNames] = useState<Record<string, string>>({});
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [thumbnail, setThumbnail] = useState<File | null>(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchCategoryById({ id: parseInt(id), locale: language }));
    }
  }, []);

  useEffect(() => {
    if (category && id) {
      if (typeof category.name === "object") {
        setNames(category.name as Record<string, string>);
      } else {
        console.error("Category name is not of expected type.");
      }
    }
  }, [category, id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id && !thumbnail) {
      toast.error(t.thumbnailRequired);
      return;
    }

    const categoryData = {
      name: names,
    };

    let resultAction;
    if (id) {
      resultAction = await dispatch(
        patchCategory({ id: parseInt(id), categoryData })
      );
    } else {
      resultAction = await dispatch(postCategory(categoryData));
    }

    if (
      postCategory.fulfilled.match(resultAction) ||
      patchCategory.fulfilled.match(resultAction)
    ) {
      const categoryId = id ? parseInt(id) : resultAction.payload.id;

      if (thumbnail) {
        try {
          await uploadCategoryThumbnail(categoryId, thumbnail);
          toast.success(t.thumbnailSuccess);
        } catch (error) {
          console.error("Failed to upload thumbnail:", error);
          toast.error(t.thumbnailError);
        }
      }

      toast.success(t.categorySuccess);
      navigate("/categories-list");
    }
  };

  const handleNavigateToSubcategories = () => {
    navigate("/subcategories-list", {
      state: {
        names,
        subcategories: category?.subCategories || [],
        id,
      },
    });
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <ToastContainer />
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow">
        <form
          id="add-category-form"
          className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-9xl flex flex-col relative"
          onSubmit={handleSubmit}
        >
          <div className="flex items-center mb-2">
            <FaArrowLeft
              className="text-gray-600 cursor-pointer mr-2"
              onClick={() => navigate("/categories-list")}
            />
            <h1 className="text-xl font-semibold">{t.title}</h1>
          </div>
          <div className="mb-4 flex justify-start space-x-2">
            <button
              type="button"
              className="bg-purple text-white px-4 py-2 rounded font-medium shadow-sm"
            >
              {t.details}
            </button>
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded font-medium shadow-sm dark:bg-white dark:text-black"
              onClick={handleNavigateToSubcategories}
            >
              {t.subcategories}
            </button>
          </div>
          <div className="mb-2 relative">
            <label
              htmlFor="name"
              className="block mb-1 text-base font-thin ml-2"
            >
              {t.name}
            </label>
            <div className="relative">
              <input
                type="text"
                id="name"
                className="w-full pl-3 pr-3 py-2 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-0 dark:bg-darkBackground dark:border-purple"
                value={names[language] || ""}
                onChange={(e) =>
                  setNames({ ...names, [language]: e.target.value })
                }
                onClick={() => setIsNameModalOpen(true)}
                autoComplete="off"
              />
              <FaGlobe className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>
          <div className="mb-2">
            <label
              htmlFor="thumbnail"
              className="block mb-1 text-base font-thin ml-2"
            >
              {t.thumbnail}
            </label>
            <input
              type="file"
              id="thumbnail"
              accept="image/*"
              required={!id}
              className="w-full pl-3 pr-3 py-2 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-0 dark:bg-darkBackground dark:border-purple"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setThumbnail(e.target.files[0]);
                }
              }}
            />
          </div>
          {id && (
            <img
              src={`${getBaseUrlWithoutApi()}/categories/${id}/icon.svg`}
              alt=""
              className="size-12 bg-white rounded-md shadow-md p-1"
            />
          )}
          <div className="absolute bottom-0 -mb-4 right-8 flex space-x-2">
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded font-medium shadow-sm dark:bg-white dark:text-black"
              onClick={() => navigate("/categories-list")}
            >
              {t.discard}
            </button>
            <button
              type="submit"
              className="bg-purple text-white px-4 py-2 rounded font-medium shadow-sm"
              form="add-category-form"
            >
              {t.save}
            </button>
          </div>
        </form>
      </div>
      <AddLanguageModal
        isOpen={isNameModalOpen}
        onClose={() => setIsNameModalOpen(false)}
        translations={t}
        mode="name"
        values={names}
        setValues={setNames}
      />
    </div>
  );
};

export default AddCategory;
